/**
 * Faststarts - One-click pre-populated searches
 * Progressive disclosure with animated reveal
 * 
 * Brand Guidelines:
 * - Playfair Display typography
 * - Purple (#5a20ff) primary
 * - Minimal, elegant presentation
 */

import React, { useState } from 'react';
import { 
  Sparkles, 
  Users, 
  ChevronDown,
  Crown,
  Code,
  Stethoscope,
  DollarSign,
  Target,
  Globe,
  Briefcase,
  TrendingUp
} from 'lucide-react';

// Simple faststarts - each triggers the correct mode
const FASTSTARTS = [
  // Top row - always visible (4 most popular)
  {
    id: 'ceos',
    query: 'CEOs at fintech companies with 50-200 employees',
    label: 'Fintech CEOs',
    mode: 'people',
    icon: Crown,
  },
  {
    id: 'ai-engineers',
    query: 'AI engineers at FAANG companies',
    label: 'AI Engineers',
    mode: 'people',
    icon: Code,
  },
  {
    id: 'remote-eng',
    query: 'Remote software engineering jobs',
    label: 'Remote Jobs',
    mode: 'jobs',
    icon: Globe,
  },
  {
    id: 'funding',
    query: 'Series A and Series B funding rounds this month',
    label: 'Fresh Funding',
    mode: 'demand',
    icon: DollarSign,
  },
  // Hidden row - revealed on expand
  {
    id: 'vp-sales',
    query: 'VP of Sales at SaaS companies with 100-500 employees',
    label: 'VP Sales',
    mode: 'people',
    icon: Target,
  },
  {
    id: 'nurses',
    query: 'Registered nurses in Texas',
    label: 'RN Nurses',
    mode: 'people',
    icon: Stethoscope,
  },
  {
    id: 'product-roles',
    query: 'Product Manager roles at startups',
    label: 'PM Roles',
    mode: 'jobs',
    icon: Briefcase,
  },
  {
    id: 'hiring',
    query: 'Tech companies with hiring signals',
    label: 'Hiring Surge',
    mode: 'demand',
    icon: TrendingUp,
  },
];

interface FaststartsProps {
  onSelect: (query: string, mode: string) => void;
  className?: string;
  hideFooter?: boolean;
}

export default function Faststarts({ onSelect, className = '', hideFooter = false }: FaststartsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const visibleItems = isExpanded ? FASTSTARTS : FASTSTARTS.slice(0, 4);
  
  return (
    <div className={`w-full ${className}`}>
      {/* Minimal pill-style buttons */}
      <div className="flex flex-wrap justify-center gap-2">
        {visibleItems.map((faststart, index) => {
          const Icon = faststart.icon;
          return (
            <button
              key={faststart.id}
              onClick={() => onSelect(faststart.query, faststart.mode)}
              className="group flex items-center gap-2 px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-black/5 rounded-full text-sm transition-all duration-300 hover:border-[#5a20ff]/30 hover:bg-white hover:shadow-md hover:shadow-[#5a20ff]/10 hover:scale-[1.03]"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                animationDelay: isExpanded && index >= 4 ? `${(index - 4) * 50}ms` : '0ms',
                animation: isExpanded && index >= 4 ? 'fadeSlideIn 0.3s ease-out forwards' : 'none',
                opacity: isExpanded && index >= 4 ? 0 : 1,
              }}
            >
              <Icon className="w-4 h-4 text-[#5a20ff]/70 group-hover:text-[#5a20ff] transition-colors" />
              <span className="font-medium text-black/70 group-hover:text-black transition-colors">
                {faststart.label}
              </span>
            </button>
          );
        })}
        
        {/* Expand/Collapse toggle */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-black/40 hover:text-[#5a20ff] transition-all duration-300"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          <span>{isExpanded ? 'Less' : 'More'}</span>
          <ChevronDown 
            className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
          />
        </button>
      </div>
      
      {/* Footer - Powered by EQ.app + Community Link (optional) */}
      {!hideFooter && (
        <div className="mt-8 flex items-center justify-center gap-4 text-center">
          <a 
            href="https://eq.app"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-black/30 hover:text-[#5a20ff] transition-colors duration-200"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <Sparkles className="w-3 h-3" />
            Powered by EQ.app
          </a>
          <span className="text-black/15">·</span>
          <a 
            href="https://marcussawyerr.substack.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-black/30 hover:text-[#5a20ff] transition-colors duration-200"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <Users className="w-3 h-3" />
            Join our community
          </a>
        </div>
      )}
      
      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}
